import { useState, ChangeEvent, useEffect, KeyboardEvent } from 'react';
import {
  Modal,
  Form,
  Button as BootstrapButton,
  Row,
  Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  AIModelsTable,
  aiModelsTableColumns
} from 'components/tables/AIModelsTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  AIAdapterConfig,
  AdapterType,
  getAllSttConfigs,
  getAllLlmConfigs,
  getAllTtsConfigs,
  getAllSummaryConfigs,
  createSttConfig,
  createLlmConfig,
  createTtsConfig,
  createSummaryConfig,
  getAvailableIAAdapters,
  GroupedAdapters,
  IAAdapter
} from 'services/hvaagent.service';
import Button from 'components/base/Button';

type AIModelWithType = AIAdapterConfig & {
  type: AdapterType;
};

// Configuración de campos por tipo de adaptador
const getDefaultFormData = (type: AdapterType): Partial<AIAdapterConfig> => {
  const base = {
    configName: '',
    description: '',
    adapterCode: '',
    active: true
  };

  switch (type) {
    case 'stt':
      return {
        ...base,
        sttProvider: 'azure-speech',
        apiKey: '',
        azureKey: '',
        region: 'westeurope',
        endpoint: '',
        language: 'es-ES',
        model: '',
        deploymentName: '',
        apiVersion: '2024-02-01',
        // Azure Speech specific
        profanityOption: 'Masked',
        enableAudioLogging: false,
        enableDictation: true,
        silenceTimeoutMs: 200,
        maxSilenceTimeoutMs: 30000,
        supportedLanguages: 'es-ES,en-GB,el-GR',
        autoDetectLanguage: false,
        // OpenAI Whisper specific
        audioFormat: 'pcm16',
        sampleRate: 24000,
        noiseReduction: 'near_field',
        transcribePrompt: '',
        vadThreshold: 0.1,
        vadPrefixPadding: 0,
        vadSilenceDuration: 500
      };
    case 'llm':
      return {
        ...base,
        apiKey: '',
        endpoint: '',
        deployment: '',
        deploymentName: '',
        apiVersion: '2024-10-21',
        model: 'gpt-4o',
        temperature: 0.1,
        maxTokens: 500,
        topP: 0.95,
        frequencyPenalty: 0,
        presencePenalty: 0
      };
    case 'tts':
      return {
        ...base,
        ttsProvider: 'elevenlabs',
        apiKey: '',
        // ElevenLabs specific
        baseUrl: 'https://api.elevenlabs.io/v1',
        defaultVoiceId: '',
        model: 'eleven_flash_v2_5',
        outputFormat: 'pcm_16000',
        languageCode: 'es',
        stability: 0.7,
        similarityBoost: 0.8,
        style: 0,
        speakerBoost: false,
        speed: 1.1,
        textNormalization: 'auto',
        useSsml: false,
        inactivityTimeout: 20,
        syncAlignment: true,
        autoMode: false,
        enableLogging: false,
        // Azure TTS specific
        region: 'westeurope',
        defaultVoice: 'es-ES-LiaNeural',
        streamingEndpoint: '',
        streamingEnabled: true
      };
    case 'summary':
      return {
        ...base,
        apiKey: '',
        endpoint: '',
        deployment: '',
        deploymentName: '',
        apiVersion: '2024-10-21',
        model: 'gpt-4.1-mini',
        temperature: 0.1,
        maxTokens: 2000,
        topP: 0.95,
        frequencyPenalty: 0,
        presencePenalty: 0
      };
    default:
      return base;
  }
};

const AIModelsList = () => {
  const [configs, setConfigs] = useState<AIModelWithType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState<AdapterType>('stt');
  const [formData, setFormData] = useState<Partial<AIAdapterConfig>>(
    getDefaultFormData('stt')
  );

  const [availableAdapters, setAvailableAdapters] = useState<GroupedAdapters>({
    STT: [],
    LLM: [],
    TTS: []
  });

  const loadConfigs = async () => {
    try {
      const [sttConfigs, llmConfigs, ttsConfigs, summaryConfigs] =
        await Promise.all([
          getAllSttConfigs(),
          getAllLlmConfigs(),
          getAllTtsConfigs(),
          getAllSummaryConfigs()
        ]);

      const allConfigs: AIModelWithType[] = [
        ...sttConfigs.map(c => ({ ...c, type: 'stt' as AdapterType })),
        ...llmConfigs.map(c => ({ ...c, type: 'llm' as AdapterType })),
        ...ttsConfigs.map(c => ({ ...c, type: 'tts' as AdapterType })),
        ...summaryConfigs.map(c => ({ ...c, type: 'summary' as AdapterType }))
      ];

      setConfigs(allConfigs);
    } catch (error) {
      console.error('Error fetching AI adapter configurations:', error);
    }
  };

  const loadAvailableAdapters = async () => {
    try {
      const adapters = await getAvailableIAAdapters();
      setAvailableAdapters(adapters);
    } catch (error) {
      console.error('Error loading available adapters:', error);
    }
  };

  useEffect(() => {
    loadConfigs();
    loadAvailableAdapters();
  }, []);

  // Función para obtener los adaptadores disponibles según el tipo de configuración
  const getAdaptersForType = (type: AdapterType): IAAdapter[] => {
    switch (type) {
      case 'stt':
        return availableAdapters.STT || [];
      case 'llm':
      case 'summary': // Summary también usa adaptadores LLM
        return availableAdapters.LLM || [];
      case 'tts':
        return availableAdapters.TTS || [];
      default:
        return [];
    }
  };

  const table = useAdvanceTable({
    data: configs,
    columns: aiModelsTableColumns(loadConfigs),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowModal = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedType('stt');
    setFormData(getDefaultFormData('stt'));
  };

  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as AdapterType;
    setSelectedType(newType);
    const defaultData = getDefaultFormData(newType);
    // Limpiar el adapterCode para que el usuario seleccione uno nuevo
    setFormData({ ...defaultData, adapterCode: '' });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    let processedValue: string | number | boolean = value;

    // Procesar según el tipo de input
    if (type === 'number') {
      processedValue = value === '' ? '' : parseFloat(value);
    } else if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const configData = { ...formData };

      switch (selectedType) {
        case 'stt':
          await createSttConfig(configData);
          break;
        case 'llm':
          await createLlmConfig(configData);
          break;
        case 'tts':
          await createTtsConfig(configData);
          break;
        case 'summary':
          await createSummaryConfig(configData);
          break;
      }

      await loadConfigs();
      handleCloseModal();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 409) {
        alert(
          `A configuration with the name "${formData.configName}" already exists.`
        );
      } else {
        alert('Error creating configuration. Please try again.');
      }
      console.error('Error creating AI adapter configuration:', error);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search AI Adapter Configurations"
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyPress}
              />
              <div className="ms-auto order-2">
                <Button
                  variant="primary"
                  className="p-2"
                  onClick={handleShowModal}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                  Create New Configuration
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <AIModelsTable />
          </div>
        </AdvanceTableProvider>
      </div>

      {/* Modal de Creación */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        scrollable
      >
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Create New AI Adapter Configuration</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            {/* Sección: Información Básica */}
            <div className="mb-2">
              <h5 className="mb-3">Basic Information</h5>
              <Row>
                <Col md={4} className="pe-0">
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      name="type"
                      value={selectedType}
                      onChange={handleTypeChange}
                      required
                    >
                      <option value="stt">STT - Speech to Text</option>
                      <option value="llm">LLM - Language Model</option>
                      <option value="tts">TTS - Text to Speech</option>
                      <option value="summary">
                        Analysis - Summary & Analysis
                      </option>
                    </Form.Select>
                    <label>Adapter Type *</label>
                  </Form.Floating>
                </Col>
                <Col md={4} className="pe-0">
                  <Form.Floating className="mb-2">
                    <Form.Control
                      className="bg-body"
                      type="text"
                      name="configName"
                      value={formData.configName || ''}
                      onChange={handleInputChange}
                      placeholder="Configuration Name"
                      required
                    />
                    <label>Configuration Name *</label>
                  </Form.Floating>
                  <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10">
                    Unique identifier (e.g., azure-stt-prod-eu)
                  </Form.Text>
                </Col>
                <Col md={4}>
                  <Form.Floating className="mb-2">
                    <Form.Select
                      className="bg-body"
                      name="adapterCode"
                      value={formData.adapterCode || ''}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select adapter...</option>
                      {getAdaptersForType(selectedType).map(adapter => (
                        <option key={adapter.id} value={adapter.adapterCode}>
                          {adapter.adapterCode} - {adapter.adapterName}
                        </option>
                      ))}
                    </Form.Select>
                    <label>Adapter Code *</label>
                  </Form.Floating>
                  <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10">
                    Select the technical adapter code
                  </Form.Text>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Floating className="mb-3">
                    <Form.Control
                      className="bg-body"
                      type="text"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      placeholder="Description"
                    />
                    <label>Description</label>
                  </Form.Floating>
                </Col>
              </Row>
            </div>
            <div
              className="bg-body rounded-3 p-4 border scrollbar mb-2"
              style={{ maxHeight: '480px' }}
            >
              {/* Campos específicos según el tipo */}
              {selectedType === 'stt' && (
                <>
                  <h5 className="mb-3">STT Configuration</h5>

                  {/* Selector de proveedor STT */}
                  <Form.Floating className="mb-4">
                    <Form.Select
                      name="sttProvider"
                      value={formData.sttProvider || 'azure-speech'}
                      onChange={e => {
                        const provider = e.target.value;
                        setFormData({
                          ...formData,
                          sttProvider: provider,
                          adapterCode:
                            provider === 'azure-speech'
                              ? 'azure-speech'
                              : 'openai-whisper',
                          // Valores fijos para OpenAI
                          ...(provider === 'openai-whisper' && {
                            audioFormat: 'pcm16',
                            sampleRate: 24000
                          })
                        });
                      }}
                    >
                      <option value="azure-speech">
                        Microsoft Azure Speech Services
                      </option>
                      <option value="openai-whisper">
                        Azure OpenAI Whisper/Transcribe
                      </option>
                    </Form.Select>
                    <label>
                      STT Provider <span className="text-danger">*</span>
                    </label>
                  </Form.Floating>

                  {/* Configuración Azure Speech Services */}
                  {formData.sttProvider === 'azure-speech' && (
                    <>
                      <h5 className="mb-2 text-body-tertiary px-1">
                        Azure Speech Services
                      </h5>
                      <Row className="mb-2">
                        <Col md={4} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="password"
                              name="apiKey"
                              value={formData.apiKey || ''}
                              onChange={handleInputChange}
                              placeholder="Enter your Azure Speech API key"
                              required
                            />
                            <label>
                              Speech API Key{' '}
                              <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={4} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="region"
                              value={formData.region || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., westeurope, eastus"
                              required
                            />
                            <label>
                              Region <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={4}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="endpoint"
                              value={formData.endpoint || ''}
                              onChange={handleInputChange}
                              placeholder="Endpoint"
                            />
                            <label>Endpoint URL</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="language"
                              value={formData.language || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., es-ES, en-US, en-GB"
                              required
                            />
                            <label>
                              Language <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                          <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10 lh-sm">
                            Language code (BCP-47)
                          </Form.Text>
                        </Col>
                        <Col md={3} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Select
                              name="profanityOption"
                              value={formData.profanityOption || 'Masked'}
                              onChange={handleInputChange}
                            >
                              <option value="Masked">Masked</option>
                              <option value="Removed">Removed</option>
                              <option value="Raw">Raw</option>
                            </Form.Select>
                            <label>Profanity Filter</label>
                          </Form.Floating>
                        </Col>
                        <Col md={3} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="supportedLanguages"
                              value={formData.supportedLanguages || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., es-ES,en-GB"
                            />
                            <label>Supported Languages</label>
                          </Form.Floating>
                          <Form.Text className="text-muted d-block mt-n2 mb-2 ps-2 fs-10 lh-sm">
                            Comma-separated language codes
                          </Form.Text>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="silenceTimeoutMs"
                              value={formData.silenceTimeoutMs || ''}
                              onChange={handleInputChange}
                              placeholder="250"
                              min="0"
                            />
                            <label>Silence Timeout (ms)</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="maxSilenceTimeoutMs"
                              value={formData.maxSilenceTimeoutMs || ''}
                              onChange={handleInputChange}
                              placeholder="30000"
                              min="0"
                            />
                            <label className="fs-11">
                              Max Silence Timeout (ms)
                            </label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <div>
                        <h6 className="mb-2 text-body-tertiary px-1">
                          Options
                        </h6>
                        <div className="rounded-3 border mb-2 p-3 bg-body-emphasis">
                          <Row>
                            <Col md={4} className="pe-0">
                              <Form.Check
                                type="checkbox"
                                name="enableAudioLogging"
                                checked={formData.enableAudioLogging || false}
                                onChange={handleInputChange}
                                label="Enable Audio Logging"
                                className="mb-0"
                              />
                            </Col>
                            <Col md={4} className="pe-0">
                              <Form.Check
                                type="checkbox"
                                name="enableDictation"
                                checked={formData.enableDictation || false}
                                onChange={handleInputChange}
                                label="Dictation Mode"
                                className="mb-0"
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Check
                                type="checkbox"
                                name="autoDetectLanguage"
                                checked={formData.autoDetectLanguage || false}
                                onChange={handleInputChange}
                                label="Auto-detect Language"
                                className="mb-0"
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Configuración Azure OpenAI Whisper/Transcribe */}
                  {formData.sttProvider === 'openai-whisper' && (
                    <>
                      <h5 className="mb-2 text-body-tertiary px-1">
                        Azure OpenAI Whisper/Transcribe
                      </h5>
                      <Row className="mb-2">
                        <Col md={6} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="password"
                              name="apiKey"
                              value={formData.apiKey || ''}
                              onChange={handleInputChange}
                              placeholder="Enter your Azure OpenAI API key"
                              required
                            />
                            <label>
                              OpenAI API Key{' '}
                              <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={6}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="endpoint"
                              value={formData.endpoint || ''}
                              onChange={handleInputChange}
                              placeholder="wss://your-resource.openai.azure.com"
                              required
                            />
                            <label>
                              Endpoint URL{' '}
                              <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={4} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="apiVersion"
                              value={formData.apiVersion || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., 2025-04-01-preview"
                            />
                            <label>API Version</label>
                          </Form.Floating>
                        </Col>
                        <Col md={4} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="model"
                              value={formData.model || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., gpt-4o-transcribe"
                            />
                            <label>Model</label>
                          </Form.Floating>
                        </Col>
                        <Col md={4}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="deploymentName"
                              value={formData.deploymentName || ''}
                              onChange={handleInputChange}
                              placeholder="Your deployment name"
                            />
                            <label>Deployment Name</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="audioFormat"
                              value="pcm16"
                              disabled
                              readOnly
                            />
                            <label>Audio Format</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="sampleRate"
                              value="24000"
                              disabled
                              readOnly
                            />
                            <label>Sample Rate (Hz)</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Select
                              name="noiseReduction"
                              value={formData.noiseReduction || 'near_field'}
                              onChange={handleInputChange}
                            >
                              <option value="near_field">Near Field</option>
                              <option value="far_field">Far Field</option>
                              <option value="none">None</option>
                            </Form.Select>
                            <label>Noise Reduction</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="vadThreshold"
                              value={formData.vadThreshold ?? ''}
                              onChange={handleInputChange}
                              placeholder="0.1"
                              min="0"
                              max="1"
                              step="0.01"
                            />
                            <label>VAD Threshold</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="vadPrefixPadding"
                              value={formData.vadPrefixPadding ?? ''}
                              onChange={handleInputChange}
                              placeholder="0"
                              min="0"
                            />
                            <label className="fs-11">
                              VAD Prefix Padding (ms)
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={2}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="vadSilenceDuration"
                              value={formData.vadSilenceDuration || ''}
                              onChange={handleInputChange}
                              placeholder="500"
                              min="0"
                            />
                            <label className="fs-11">
                              VAD Silence Duration (ms)
                            </label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              as="textarea"
                              name="transcribePrompt"
                              value={formData.transcribePrompt || ''}
                              onChange={handleInputChange}
                              placeholder="Optional context or instructions for transcription"
                              rows={1}
                              className="scrollbar"
                            />
                            <label>Transcription Prompt</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              {selectedType === 'llm' && (
                <>
                  <h5 className="mb-3">LLM Configuration (Azure AI Foundry)</h5>
                  <Row className="mb-2">
                    <Col md={6} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="password"
                          name="apiKey"
                          value={formData.apiKey || ''}
                          onChange={handleInputChange}
                          placeholder="Enter your Azure OpenAI API key"
                          required
                        />
                        <label>
                          API Key <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={6}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="endpoint"
                          value={formData.endpoint || ''}
                          onChange={handleInputChange}
                          placeholder="https://your-resource.openai.azure.com/"
                          required
                        />
                        <label>
                          Endpoint URL <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="deployment"
                          value={formData.deployment || ''}
                          onChange={handleInputChange}
                          placeholder="Your deployment name"
                          required
                        />
                        <label>
                          Deployment Name <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="model"
                          value={formData.model || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., gpt-4, gpt-35-turbo"
                        />
                        <label>Model</label>
                      </Form.Floating>
                    </Col>
                    <Col md={4}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="apiVersion"
                          value={formData.apiVersion || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2024-02-15-preview"
                        />
                        <label>API Version</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="maxTokens"
                          value={formData.maxTokens || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 4000"
                          min="1"
                          max="128000"
                        />
                        <label>Max Tokens</label>
                      </Form.Floating>
                    </Col>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="temperature"
                          value={formData.temperature ?? ''}
                          onChange={handleInputChange}
                          placeholder="0.1"
                          min="0"
                          max="2"
                          step="0.1"
                        />
                        <label>Temperature</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="topP"
                          value={formData.topP ?? ''}
                          onChange={handleInputChange}
                          placeholder="0.95"
                          min="0"
                          max="1"
                          step="0.01"
                        />
                        <label>Top P</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="frequencyPenalty"
                          value={formData.frequencyPenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Frequency Penalty</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="presencePenalty"
                          value={formData.presencePenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Presence Penalty</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                </>
              )}

              {selectedType === 'tts' && (
                <>
                  <h5 className="mb-3">TTS Configuration</h5>

                  {/* Selector de proveedor TTS */}
                  <Form.Floating className="mb-3">
                    <Form.Select
                      name="ttsProvider"
                      value={formData.ttsProvider || 'elevenlabs'}
                      onChange={e => {
                        const provider = e.target.value;
                        setFormData({
                          ...formData,
                          ttsProvider: provider,
                          adapterCode:
                            provider === 'elevenlabs'
                              ? 'elevenlabs-ws'
                              : 'azure-tts'
                        });
                      }}
                    >
                      <option value="elevenlabs">ElevenLabs</option>
                      <option value="azure-tts">Microsoft Azure TTS</option>
                    </Form.Select>
                    <label>
                      TTS Provider <span className="text-danger">*</span>
                    </label>
                  </Form.Floating>

                  {/* Configuración ElevenLabs */}
                  {formData.ttsProvider === 'elevenlabs' && (
                    <>
                      <h5 className="mb-2 text-body-tertiary px-1">
                        ElevenLabs Configuration
                      </h5>
                      <Row className="mb-2">
                        <Col md={6} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="password"
                              name="apiKey"
                              value={formData.apiKey || ''}
                              onChange={handleInputChange}
                              placeholder="Enter your ElevenLabs API key"
                              required
                            />
                            <label>
                              API Key <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={6}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="baseUrl"
                              value={formData.baseUrl || ''}
                              onChange={handleInputChange}
                              placeholder="https://api.elevenlabs.io/v1"
                            />
                            <label>Base URL</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={3} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="defaultVoiceId"
                              value={formData.defaultVoiceId || ''}
                              onChange={handleInputChange}
                              placeholder="ElevenLabs voice ID"
                              required
                            />
                            <label>
                              Default Voice ID{' '}
                              <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={3} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Select
                              name="model"
                              value={formData.model || ''}
                              onChange={handleInputChange}
                            >
                              <option value="eleven_flash_v2_5">
                                Eleven Flash v2.5
                              </option>
                              <option value="eleven_turbo_v2_5">
                                Eleven Turbo v2.5
                              </option>
                              <option value="eleven_multilingual_v2">
                                Eleven Multilingual v2
                              </option>
                            </Form.Select>
                            <label>Model</label>
                          </Form.Floating>
                        </Col>
                        <Col md={3} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Select
                              name="outputFormat"
                              value={formData.outputFormat || ''}
                              onChange={handleInputChange}
                            >
                              <option value="pcm_16000">PCM 16kHz</option>
                            </Form.Select>
                            <label>Output Format</label>
                          </Form.Floating>
                        </Col>
                        <Col md={3}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="languageCode"
                              value={formData.languageCode || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., es, en, fr"
                            />
                            <label>Language Code (ISO 639-1)</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-3">
                            <Form.Control
                              type="number"
                              name="stability"
                              value={formData.stability || ''}
                              onChange={handleInputChange}
                              placeholder="0.7"
                              min="0"
                              max="1"
                              step="0.1"
                            />
                            <label>Stability</label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Voice consistency (0-1)
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-3">
                            <Form.Control
                              type="number"
                              name="similarityBoost"
                              value={formData.similarityBoost || ''}
                              onChange={handleInputChange}
                              placeholder="0.8"
                              min="0"
                              max="1"
                              step="0.1"
                            />
                            <label>Similarity Boost</label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Voice similarity (0-1)
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="style"
                              value={formData.style ?? ''}
                              onChange={handleInputChange}
                              placeholder="0"
                              min="0"
                              max="1"
                              step="0.1"
                            />
                            <label>Style</label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Style exaggeration (0-1)
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="speed"
                              value={formData.speed || ''}
                              onChange={handleInputChange}
                              placeholder="1.1"
                              min="0.25"
                              max="4"
                              step="0.05"
                            />
                            <label>Speed</label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Speaking speed (0.25-4)
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                        <Col md={2} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Select
                              name="textNormalization"
                              value={formData.textNormalization || 'auto'}
                              onChange={handleInputChange}
                            >
                              <option value="auto">Auto</option>
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </Form.Select>
                            <label>Text Normalization</label>
                          </Form.Floating>
                        </Col>
                        <Col md={2}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="number"
                              name="inactivityTimeout"
                              value={formData.inactivityTimeout || ''}
                              onChange={handleInputChange}
                              placeholder="20"
                              min="0"
                            />
                            <label className="fs-11">
                              Inactivity Timeout (s)
                            </label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <h6 className="mb-2 text-body-tertiary px-1">Options</h6>
                      <div className="rounded-3 border mb-0 p-3 bg-body-emphasis">
                        <Row>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              name="speakerBoost"
                              checked={formData.speakerBoost || false}
                              onChange={handleInputChange}
                              label="Speaker Boost"
                              className="mb-0"
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              name="useSsml"
                              checked={formData.useSsml || false}
                              onChange={handleInputChange}
                              label="SSML Parsing"
                              className="mb-0"
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              name="syncAlignment"
                              checked={formData.syncAlignment || false}
                              onChange={handleInputChange}
                              label="Sync Alignment"
                              className="mb-0"
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              name="autoMode"
                              checked={formData.autoMode || false}
                              onChange={handleInputChange}
                              label="Auto Mode"
                              className="mb-0"
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Check
                              type="checkbox"
                              name="enableLogging"
                              checked={formData.enableLogging || false}
                              onChange={handleInputChange}
                              label="Enable Logging"
                              className="mb-0"
                            />
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}

                  {/* Configuración Azure TTS */}
                  {formData.ttsProvider === 'azure-tts' && (
                    <>
                      <h5 className="mb-2 text-body-tertiary px-1">
                        Microsoft Azure TTS
                      </h5>
                      <Row className="mb-2">
                        <Col md={6} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="password"
                              name="apiKey"
                              value={formData.apiKey || ''}
                              onChange={handleInputChange}
                              placeholder="Enter your Azure TTS API key"
                              required
                            />
                            <label>
                              API Key <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                        <Col md={6}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="region"
                              value={formData.region || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., westeurope, eastus"
                              required
                            />
                            <label>
                              Region <span className="text-danger">*</span>
                            </label>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={6} className="pe-0">
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="defaultVoice"
                              value={formData.defaultVoice || ''}
                              onChange={handleInputChange}
                              placeholder="e.g., es-ES-LiaNeural"
                              required
                            />
                            <label>
                              Default Voice{' '}
                              <span className="text-danger">*</span>
                            </label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Azure neural voice name
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                        <Col md={6}>
                          <Form.Floating className="mb-2">
                            <Form.Control
                              type="text"
                              name="streamingEndpoint"
                              value={formData.streamingEndpoint || ''}
                              onChange={handleInputChange}
                              placeholder="https://region.tts.speech.microsoft.com/cognitiveservices/v1"
                            />
                            <label>Streaming Endpoint</label>
                            <Form.Text className="text-muted d-block mt-0 mb-2 ps-2 fs-10 lh-sm">
                              Custom streaming endpoint (optional)
                            </Form.Text>
                          </Form.Floating>
                        </Col>
                      </Row>
                      <div className="mb-2">
                        <Form.Label>Options</Form.Label>
                        <div className="rounded-3 border mb-2 p-3 pb-2 bg-body-emphasis">
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-0">
                                <Form.Check
                                  type="checkbox"
                                  name="streamingEnabled"
                                  checked={formData.streamingEnabled || false}
                                  onChange={handleInputChange}
                                  label="Enable Streaming"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {selectedType === 'summary' && (
                <>
                  <h5 className="mb-3">
                    Analysis Configuration (Azure AI Foundry)
                  </h5>
                  <Row className="mb-2">
                    <Col md={6} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="password"
                          name="apiKey"
                          value={formData.apiKey || ''}
                          onChange={handleInputChange}
                          placeholder="Enter your Azure OpenAI API key"
                          required
                        />
                        <label>
                          API Key <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={6}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="endpoint"
                          value={formData.endpoint || ''}
                          onChange={handleInputChange}
                          placeholder="https://your-resource.openai.azure.com/"
                          required
                        />
                        <label>
                          Endpoint URL <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="deployment"
                          value={formData.deployment || ''}
                          onChange={handleInputChange}
                          placeholder="Your deployment name"
                          required
                        />
                        <label>
                          Deployment Name <span className="text-danger">*</span>
                        </label>
                      </Form.Floating>
                    </Col>
                    <Col md={4} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="model"
                          value={formData.model || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., gpt-4, gpt-35-turbo"
                        />
                        <label>Model</label>
                      </Form.Floating>
                    </Col>
                    <Col md={4}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="text"
                          name="apiVersion"
                          value={formData.apiVersion || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2024-02-15-preview"
                        />
                        <label>API Version</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="maxTokens"
                          value={formData.maxTokens || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., 2000"
                          min="1"
                          max="128000"
                        />
                        <label>Max Tokens</label>
                      </Form.Floating>
                    </Col>
                    <Col md={3} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="temperature"
                          value={formData.temperature || ''}
                          onChange={handleInputChange}
                          placeholder="0.1"
                          min="0"
                          max="2"
                          step="0.1"
                        />
                        <label>Temperature</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="topP"
                          value={formData.topP || ''}
                          onChange={handleInputChange}
                          placeholder="0.95"
                          min="0"
                          max="1"
                          step="0.01"
                        />
                        <label>Top P</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2} className="pe-0">
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="frequencyPenalty"
                          value={formData.frequencyPenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Frequency Penalty</label>
                      </Form.Floating>
                    </Col>
                    <Col md={2}>
                      <Form.Floating className="mb-2">
                        <Form.Control
                          type="number"
                          name="presencePenalty"
                          value={formData.presencePenalty ?? ''}
                          onChange={handleInputChange}
                          placeholder="0"
                          min="-2"
                          max="2"
                          step="0.1"
                        />
                        <label>Presence Penalty</label>
                      </Form.Floating>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <BootstrapButton variant="primary" type="submit">
              Create Configuration
            </BootstrapButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AIModelsList;
